import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import AdminCreateUserForm from "./AdminCreateUserForm";
import BulkImportModal from "../BulkImportModal";
import { Upload } from "lucide-react";

const ROLES = ["buyer", "seller", "admin"];

export default function AdminUsers({ api, t }) {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [orgs, setOrgs] = useState([]);
  const [showImport, setShowImport] = useState(false);

  const load = () => {
    api.get("/admin/users", { params: search ? { search } : {} }).then((r) => {
      setItems(r.data);
      setSelected(new Set());
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    api.get("/admin/organizations").then((r) => setOrgs(r.data || [])).catch(() => {});
  }, [api]);

  const onCreated = (user) => {
    setItems((arr) => [user, ...arr]);
  };

  const setUserOrg = async (u, organization_id) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}/organization`, { organization_id: organization_id || null });
      setItems((arr) => arr.map((x) => x.user_id === u.user_id ? data : x));
      toast.success(t("admin.actionOk"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Select all EXCEPT current admin (can't bulk-suspend self)
  const selectableIds = () => items.filter((u) => u.user_id !== currentUser?.user_id).map((u) => u.user_id);
  const selectAll = () => setSelected(new Set(selectableIds()));
  const clearSel = () => setSelected(new Set());
  const allSelected = items.length > 0 && selected.size === selectableIds().length && selected.size > 0;

  const patch = async (u, body) => {
    const { data } = await api.patch(`/admin/users/${u.user_id}`, body);
    setItems((arr) => arr.map((x) => x.user_id === u.user_id ? data : x));
    toast.success(t("admin.actionOk"));
  };

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    if (action === "delete") {
      const ok = window.confirm(t("admin.bulk.confirmDelete", { n: selected.size }));
      if (!ok) return;
    }
    const ids = Array.from(selected);
    const { data } = await api.post("/admin/users/bulk", { ids, action });
    toast.success(t("admin.bulk.usersDone", { n: data.affected }));
    if (action === "delete") {
      setItems((arr) => arr.filter((x) => !selected.has(x.user_id)));
    } else {
      const suspended = action === "suspend";
      setItems((arr) => arr.map((x) => selected.has(x.user_id) ? { ...x, suspended } : x));
    }
    setSelected(new Set());
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <AdminCreateUserForm api={api} t={t} onCreated={onCreated} />
        <button onClick={() => setShowImport(true)} className="btn-ghost" data-testid="admin-users-import-btn">
          <Upload size={14} /> {t("bulk.import")}
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          className="input-tech flex-1"
          placeholder="Email / name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          data-testid="admin-users-search"
        />
        <button onClick={load} className="btn-ghost" data-testid="admin-users-search-btn">{t("market.search")}</button>
      </div>

      {selected.size > 0 && (
        <div className="mb-4 p-3 bg-[var(--ink)] text-[var(--bone)] flex items-center gap-3 flex-wrap" data-testid="admin-users-bulk-bar">
          <span className="label-tech text-[var(--bone)]">{selected.size} {t("admin.bulk.selected")}</span>
          <button onClick={() => runBulk("suspend")} className="btn-primary text-xs" data-testid="admin-bulk-suspend">{t("admin.bulk.suspend")}</button>
          <button onClick={() => runBulk("unsuspend")} className="btn-ghost text-xs" style={{ color: "var(--bone)", borderColor: "var(--bone)" }} data-testid="admin-bulk-unsuspend">{t("admin.bulk.unsuspend")}</button>
          <button onClick={() => runBulk("delete")} className="text-xs px-3 py-2 bg-[var(--brick)] text-[var(--bone)] hover:bg-[var(--brick)]/80 font-mono-tech uppercase tracking-[0.1em]" data-testid="admin-bulk-delete">{t("admin.bulk.delete")}</button>
          <button onClick={clearSel} className="label-tech text-[var(--bone)] ml-auto hover:underline" data-testid="admin-users-bulk-clear">{t("admin.bulk.clear")}</button>
        </div>
      )}

      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-2 cursor-pointer label-tech">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => (allSelected ? clearSel() : selectAll())}
            data-testid="admin-users-select-all"
          />
          {t("admin.bulk.selectAll")}
        </label>
      </div>

      <div className="divide-y divide-[var(--ink-200)]">
        {items.map((u) => {
          const isSelf = u.user_id === currentUser?.user_id;
          return (
            <div key={u.user_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`admin-user-${u.user_id}`}>
              <input
                type="checkbox"
                checked={selected.has(u.user_id)}
                onChange={() => !isSelf && toggle(u.user_id)}
                disabled={isSelf}
                data-testid={`admin-user-checkbox-${u.user_id}`}
              />
              <div className="flex-1 min-w-[220px]">
                <div className="font-display">
                  {u.name}
                  {u.suspended && <span className="tag tag-brick ml-2">{t("admin.suspended")}</span>}
                  {isSelf && <span className="tag tag-slate ml-2">you</span>}
                </div>
                <div className="label-tech mt-1">{u.email}</div>
              </div>
              <select className="input-tech w-auto text-xs" value={u.role || ""} onChange={(e) => patch(u, { role: e.target.value })} data-testid={`admin-user-role-${u.user_id}`}>
                <option value="" disabled>—</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select
                className="input-tech w-auto text-xs max-w-[180px]"
                value={u.organization_id || ""}
                onChange={(e) => setUserOrg(u, e.target.value)}
                data-testid={`admin-user-org-${u.user_id}`}
                title={t("admin.user.org")}
              >
                <option value="">{t("admin.createUser.orgPersonal")}</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <button
                onClick={() => patch(u, { suspended: !u.suspended })}
                disabled={isSelf}
                className="btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`admin-user-suspend-${u.user_id}`}
              >
                {u.suspended ? t("admin.unsuspend") : t("admin.suspend")}
              </button>
            </div>
          );
        })}
      </div>

      {showImport && (
        <BulkImportModal
          title={t("bulk.title.adminUsers")}
          description={t("bulk.desc.adminUsers")}
          uploadEndpoint="/admin/users/bulk-import"
          templateEndpoint="/admin/users/bulk-import/template"
          onClose={() => setShowImport(false)}
          onSuccess={() => load()}
        />
      )}
    </div>
  );
}
